/* VariantsAvailability class copied from toteme-2023 */
export class VariantsAvailability {
  constructor (variantId, data, storeAvailabilityCountries) {
    this.variantId = variantId
    this.data = data
    this.storeAvailabilityCountries = storeAvailabilityCountries
  }

  availability () {
    // eslint-disable-next-line max-len
    return this.data?.find(
      (variant) =>
        Number(variant?.node?.legacyResourceId) === Number(this.variantId),
    )
  }

  inventoryLevels () {
    return this.availability()?.node?.inventoryItem?.inventoryLevels?.edges?.map(
      (variantInventoryLevel) => variantInventoryLevel?.node,
    )
  }

  offlineInventoryLevels () {
    // eslint-disable-next-line max-len
    return this.inventoryLevels()?.filter((inventoryLevel) => {
      return (
        inventoryLevel?.location?.isActive &&
        !inventoryLevel?.location?.fulfillsOnlineOrders &&
        !inventoryLevel?.location?.name.includes('draft') &&
        !inventoryLevel?.location?.name.includes('Draft') &&
        this.storeAvailabilityCountries.includes(
          inventoryLevel?.location?.address?.countryCode,
        )
      )
    })
  }

  availableStoresFromLocations () {
    return this.offlineInventoryLevels()?.map((inventoryLevel) => {
      const quantityAvailable = inventoryLevel?.quantities?.find(
        (quantity) => quantity?.name === 'available',
      )?.quantity

      let message = ''

      const { notAvailable, limitedAvailability, available } =
        window.theme.strings.store_availability

      if (quantityAvailable === 0) {
        message = `${notAvailable} ${inventoryLevel?.location?.name}`
      } else if (quantityAvailable === 1) {
        message = `${limitedAvailability} ${inventoryLevel?.location?.name}`
      } else if (quantityAvailable > 1) {
        message = `${available} ${inventoryLevel?.location?.name}`
      }

      return {
        /*
          0 = Not available
          1-2 = Limited availability
          3 and up = Available
        */
        message,
        countryCode: inventoryLevel?.location?.address?.countryCode,
        address: inventoryLevel?.location?.address,
        quantities: inventoryLevel?.quantities,
        quantityAvailable,
      }
    })
  }

  availableStoresFromLocationsSortedByProximity (latitude, longitude) {
    if (!latitude || !longitude) {
      return this.availableStoresFromLocations()
    }

    return this.availableStoresFromLocations()
      ?.map((store) => {
        // eslint-disable-next-line max-len
        store.distance = this.calculateDistance(
          latitude,
          longitude,
          store?.address?.latitude,
          store?.address?.longitude,
        )

        return store
      })
      .sort((a, b) => a?.distance - b?.distance)
  }

  calculateDistance (latitudeFrom, longitudeFrom, latitudeTo, longitudeTo) {
    if (latitudeFrom === latitudeTo && longitudeFrom === longitudeTo) {
      return 0
    }

    const radialLatitudeFrom = (Math.PI * latitudeFrom) / 180
    const radialLatitudeTo = (Math.PI * latitudeTo) / 180
    const fromMinusTo = longitudeFrom - longitudeTo
    const radialFromMinusTo = (Math.PI * fromMinusTo) / 180
    // eslint-disable-next-line max-len, no-mixed-operators
    let distance =
      Math.sin(radialLatitudeFrom) * Math.sin(radialLatitudeTo) +
      Math.cos(radialLatitudeFrom) *
        Math.cos(radialLatitudeTo) *
        Math.cos(radialFromMinusTo)

    if (distance > 1) {
      distance = 1
    }

    distance = Math.acos(distance)
    distance = (distance * 180) / Math.PI
    distance = distance * 60 * 1.1515

    return distance
  }
}

class StoreAvailability extends window.BAO.CustomElement() {
  constructor () {
    super()
    this.variantAvailabilityData = []
    this.coordinates = null
    this.showStoreAvailability = false
    this.storeAvailabilityCountries = []
    this.storeAvailabilityLinks = false
    this.appDomain = ''
    this.productId = null
    this.storeName = ''
    this.initialState = true
    this.currentVariantId = null

    this.countryCode = null
  }

  connectedCallback () {
    super.connectedCallback()

    this.getUserCountry()

    this.showStoreAvailability =
      this.getAttribute('show-store-availability') === 'true'
    this.storeAvailabilityLinks =
      this.getAttribute('store-availability-links') === 'true'
    this.appDomain = this.getAttribute('domain')
    this.productId = Number(this.getAttribute('product-id'))
    this.storeName = this.getAttribute('store-name')
    this.initialState = this.getAttribute('initial-state') === 'true'
    this.currentVariantId = this.getAttribute('selected-variant-id')

    const countriesAttr = this.getAttribute('store-availability-countries')
    try {
      const parsed = JSON.parse(countriesAttr)
      this.storeAvailabilityCountries = Array.isArray(parsed) ? parsed : []
    } catch {
      this.storeAvailabilityCountries = (countriesAttr || '')
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean)
    }

    if (this.showStoreAvailability) this.loadData()
  }

  setupListeners () {
    super.setupListeners()

    this.onOptionChanged = this.onOptionChanged.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_CHANGED,
      this.onOptionChanged,
    )
  }

  async getUserCountry () {
    try {
      const response = await fetch('/browsing_context_suggestions.json')
      const data = await response.json()

      this.countryCode = data?.detected_values?.country?.handle || null
      this.render()
    } catch (e) {
      this.countryCode = null
    }
  }

  onOptionChanged (event) {
    const variant = event?.detail?.variant
    if (!variant) return

    this.currentVariantId = variant.id
    this.initialState = false
    this.setAttribute('aria-hidden', 'false')
    this.render()

    // Populate the drawer with updated stores
    const availability = new VariantsAvailability(
      this.currentVariantId,
      this.variantAvailabilityData,
      this.storeAvailabilityCountries,
    )

    let availableStores = availability.availableStoresFromLocations()
    if (this.coordinates) {
      availableStores =
        availability.availableStoresFromLocationsSortedByProximity(
          this.coordinates.latitude,
          this.coordinates.longitude,
        )
    }

    this.populateDrawer(availableStores || [])
  }

  async loadData () {
    try {
      const coordinatesJson = window.sessionStorage.getItem('coordinates')
      if (coordinatesJson) {
        this.coordinates = JSON.parse(coordinatesJson)
      } else if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          this.coordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }
          window.sessionStorage.setItem(
            'coordinates',
            JSON.stringify(this.coordinates),
          )
          this.render()
        })
      }

      const response = await fetch(
        `https://${this.appDomain}/api/${this.productId}/inventory?store_name=${this.storeName}`,
      )
      const data = await response.json()
      if (!data?.ok) return

      this.variantAvailabilityData = data?.data?.product?.variants?.edges

      this.render()
    } catch (err) {
      console.error('Error fetching inventory:', err)
    }
  }

  formatStorePageLink (storeMessage) {
    if (!this.storeAvailabilityLinks || !storeMessage) return storeMessage

    const storeName = storeMessage
      .replace(
        /^(Not available at |Limited availability at |Available at )/i,
        '',
      )
      .trim()

    const pageLink = storeName
      .replace(/-?store$/i, '') // removes "-store" for URL
      .trim()
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '')

    return storeMessage.replace(
      storeName,
      `<a href="/pages/${pageLink}">${storeName}</a>`,
    )
  }

  groupStoresByCity (stores) {
    const grouped = {}
    stores.forEach((store) => {
      const city = store.address?.city || 'Other'
      if (!grouped[city]) grouped[city] = []
      grouped[city].push(store)
    })
    return grouped
  }

  render () {
    this.innerHTML = ''

    if (!this.showStoreAvailability || !this.variantAvailabilityData) return

    const availability = new VariantsAvailability(
      this.currentVariantId,
      this.variantAvailabilityData,
      this.storeAvailabilityCountries,
    )

    // Get stores
    let availableStores
    availableStores = this.coordinates
      ? availability.availableStoresFromLocationsSortedByProximity(
        this.coordinates.latitude,
        this.coordinates.longitude,
      )
      : availability.availableStoresFromLocations()

    // Filter by countries
    if (
      Array.isArray(this.storeAvailabilityCountries) &&
      this.storeAvailabilityCountries.length > 0
    ) {
      availableStores = availableStores.filter((store) =>
        this.storeAvailabilityCountries.includes(store.countryCode),
      )
    }

    this.setAttribute(
      'aria-hidden',
      !availableStores || availableStores?.length === 0,
    )

    if (!availableStores || availableStores.length === 0) return

    const firstStore =
      this.initialState === true
        ? window.theme.strings.store_availability.selectSize
        : !availableStores || availableStores.length === 0
            ? window.theme.strings.store_availability.empty
            : this.formatStorePageLink(availableStores[0].message)
    const additionalStoresCount = availableStores.length - 1

    const wrapper = document.createElement('div')
    wrapper.className = 'prd-StoreAvailability_Body'
    wrapper.innerHTML = this.pin

    const message = document.createElement('span')
    message.className = 'prd-StoreAvailability_Text fz-12_160'
    message.innerHTML = firstStore
    wrapper.appendChild(message)

    if (
      additionalStoresCount > 0 &&
      this.initialState === false &&
      this.getAttribute('aria-hidden') === 'false'
    ) {
      const moreButton = document.createElement('button')
      moreButton.className = 'prd-StoreAvailability_Button fz-12_160'
      moreButton.textContent = `+ ${additionalStoresCount} store${additionalStoresCount > 1 ? 's' : ''}`

      moreButton.addEventListener('click', () => {
        this.populateDrawer(availableStores)

        setTimeout(() => {
          window.drawers.activeDrawerKey = 'store-availability'

          try {
            const dataLayer = window.dataLayer || []

            dataLayer.push({
              event: 'store_inventory_click',
              storeInventoryClick: {
                url: document.location.pathname,
              },
            })
          } catch (error) {
            console.error('Error pushing to dataLayer: ', error)
          }
        }, 0)
      })

      wrapper.appendChild(moreButton)
    }

    this.appendChild(wrapper)
  }

  populateDrawer (stores) {
    if (!this.externalEls?.drawer?.element) return

    let filteredStores = stores

    // Filter by countries
    if (this.storeAvailabilityCountries?.length) {
      filteredStores = filteredStores.filter((store) =>
        this.storeAvailabilityCountries.includes(store.countryCode),
      )
    }

    const drawerEl = this.externalEls.drawer.element
    drawerEl.innerHTML = ''

    const groupedStores = this.groupStoresByCity(filteredStores)

    Object.keys(groupedStores).forEach((city) => {
      const cityWrapper = document.createElement('div')
      cityWrapper.className = 'drw-StoreAvailability_City'

      const cityTitle = document.createElement('h3')
      cityTitle.textContent = city
      cityTitle.className = 'drw-StoreAvailability_Title fz-14_155'
      cityWrapper.appendChild(cityTitle)

      groupedStores[city].forEach((store) => {
        const outer = document.createElement('div')
        outer.className = 'prd-StoreAvailability'

        const storeWrapper = document.createElement('div')
        storeWrapper.className = 'prd-StoreAvailability_Body'
        storeWrapper.innerHTML = this.availabilityColour(
          this.quantityAvailableState(store.quantityAvailable),
        )

        const messageEl = document.createElement('span')
        messageEl.className = 'prd-StoreAvailability_Text fz-12_160'
        messageEl.innerHTML = this.formatStorePageLink(store.message)

        storeWrapper.appendChild(messageEl)
        outer.appendChild(storeWrapper)

        cityWrapper.appendChild(outer)
      })

      drawerEl.appendChild(cityWrapper)
    })
  }

  getStoreName (storeMessage) {
    const { notAvailable, limitedAvailability, available } =
      window.theme.strings.store_availability

    if (!storeMessage) return ''

    if (storeMessage.includes(' at ')) {
      return storeMessage.split(' at ')[1].trim()
    }
    if (storeMessage.includes(limitedAvailability)) {
      return storeMessage.split(limitedAvailability)[1].trim()
    }
    if (storeMessage.includes(available)) {
      return storeMessage.split(available)[1].trim()
    }
    if (storeMessage.includes(notAvailable)) {
      return storeMessage.split(notAvailable)[1].trim()
    }
    return storeMessage
  }

  availabilityColour (state) {
    return `<span class="prd-StoreAvailability_Colour prd-StoreAvailability_Colour-${state}"></span>`
  }

  quantityAvailableState (quantityAvailable) {
    if (quantityAvailable === 0) return 'notAvailable'
    if (quantityAvailable === 1) return 'limited'
    return 'available'
  }

  get pin () {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none"><path d="M7.367 3.054a4.054 4.054 0 0 0-2.155 1.058 3.625 3.625 0 0 0-.755 1.006 4.015 4.015 0 0 0-.433 2.229c.122 1.373.723 2.58 2.06 4.139.302.352 1.608 1.734 1.846 1.952l.07.065.07-.065a56.613 56.613 0 0 0 1.818-1.918c.969-1.126 1.529-2.031 1.845-2.982.325-.976.343-2 .049-2.871-.465-1.381-1.693-2.392-3.174-2.615a5.42 5.42 0 0 0-1.241.002M8.64 4.066c.604.146 1.05.381 1.461.771.526.499.823 1.127.882 1.871.12 1.495-.697 3.039-2.667 5.042l-.318.323-.316-.323C5.702 9.722 4.897 8.2 5.017 6.708c.031-.386.126-.741.289-1.071.162-.329.307-.525.581-.788.345-.33.73-.56 1.166-.697.187-.058.28-.08.6-.137.139-.024.82.011.987.051M7.773 5.52a1.493 1.493 0 0 0-1.108.813c-.345.702-.119 1.504.546 1.938.239.156.454.214.789.214.21 0 .324-.014.455-.055.366-.116.705-.41.88-.763.346-.701.119-1.504-.547-1.939a1.564 1.564 0 0 0-1.015-.208" fill-rule="evenodd" fill="currentColor"></path></svg>'
  }
}

customElements.define('store-availability', StoreAvailability)
